import React, { useState, useEffect } from 'react';
import { App, Table, Tag, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { formatCurrency } from '../utils/format';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';

interface PricingOption {
  id: string;
  unit: string;
  unit_price: number;
}

interface Consumption {
  id: string;
}

interface ExecutionResource {
  id: string;
  name: string;
  description: string;
  type: string;
  resource_number: string;
  metadata: any;
  service_provider: {
    id: string;
    name: string;
  };
  pricing_options: PricingOption[];
  consumptions: Consumption[];
}

const { Search } = Input;

const ExecutionResources: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [resources, setResources] = useState<ExecutionResource[]>([]);
  const [loading, setLoading] = useState(false);
  const { setSearch: setSearchText, debouncedSearch: debouncedSearchText } = useDebouncedSearch();

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/execution-resources');
      setResources(response.data.execution_resources || []);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('common.noData'));
    } finally {
      setLoading(false);
    }
  };

  const filteredResources = resources.filter((r) =>
    r.name.toLowerCase().includes(debouncedSearchText.toLowerCase())
  );

  const typeColorMap: Record<string, string> = {
    consumable: 'blue',
    tool: 'cyan',
    power_tool: 'geekblue',
    vehicle: 'orange',
    man_power: 'green',
  };

  const typeLabelMap: Record<string, string> = {
    consumable: t('executionResources.typeConsumable'),
    tool: t('executionResources.typeTool'),
    power_tool: t('executionResources.typePowerTool'),
    vehicle: t('executionResources.typeVehicle'),
    man_power: t('executionResources.typeManPower'),
  };

  const columns = [
    {
      title: t('executionResources.resourceNumber'),
      dataIndex: 'resource_number',
      key: 'resource_number',
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('executionResources.type'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={typeColorMap[type] || 'default'}>{typeLabelMap[type] || type}</Tag>
      ),
    },
    {
      title: t('executionResources.serviceProvider'),
      key: 'service_provider',
      render: (_: any, record: ExecutionResource) => record.service_provider?.name || '-',
    },
    {
      title: t('executionResources.pricingOptions'),
      key: 'pricing_options_count',
      render: (_: any, record: ExecutionResource) => record.pricing_options?.length || 0,
    },
  ];

  const expandedRowRender = (record: ExecutionResource) => {
    const pricingColumns = [
      { title: t('executionResources.unit'), dataIndex: 'unit', key: 'unit' },
      {
        title: t('executionResources.unitPrice'),
        dataIndex: 'unit_price',
        key: 'unit_price',
        render: (price: number) => price != null ? formatCurrency(price, 'EUR', i18n.language) : '-',
      },
    ];

    return (
      <div style={{ padding: '8px 0' }}>
        {record.pricing_options && record.pricing_options.length > 0 && (
          <>
            <strong>{t('executionResources.pricingOptions')}:</strong>
            <Table
              columns={pricingColumns}
              dataSource={record.pricing_options}
              rowKey="id"
              pagination={false}
              size="small"
              style={{ marginTop: 8 }}
            />
          </>
        )}
        {record.consumptions && record.consumptions.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <strong>{t('executionResources.consumptions')}: </strong>
            <span>{record.consumptions.length}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('menu.executionResources')}</h1>
      </div>

      <Search
        placeholder={t('common.search')}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
        allowClear
      />

      <Table
        columns={columns}
        dataSource={filteredResources}
        loading={loading}
        rowKey="id"
        expandable={{ expandedRowRender }}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
};

export default ExecutionResources;
